/**
 * Folder Management View Component
 *
 * Main view for managing folder structure and denomination inheritance.
 * Includes folder tree, create/edit/delete operations, and bulk update warnings.
 */

import React, { useEffect } from 'react';
import { useFolderStore } from '../stores/folderStore';
import { useAuthStore } from '../stores/authStore';
import FolderTree from './FolderTree';
import FolderDialog from './FolderDialog';
import FolderWarningDialog from './FolderWarningDialog';

export const FolderManagementView: React.FC = () => {
  const { user } = useAuthStore();
  const {
    selectedFolder,
    total,
    isLoading,
    loadFolders,
    openCreateDialog,
    deleteSelectedFolder,
    isDeleteDialogOpen,
    closeDeleteDialog,
    isSaving,
  } = useFolderStore();

  // Load folders on mount
  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  const handleDelete = async () => {
    await deleteSelectedFolder();
  };

  return (
    <div className="flex flex-col h-full bg-dark-bg text-dark-text">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-dark-border">
        <div>
          <h1 className="text-2xl font-bold">Folder Management</h1>
          <p className="text-sm text-dark-text-muted mt-1">
            {user?.is_superuser
              ? `Organize files with denomination inheritance • ${total} folder${total !== 1 ? 's' : ''}`
              : `Organize your personal documents • ${total} folder${total !== 1 ? 's' : ''}`
            }
          </p>
        </div>
        <button
          onClick={openCreateDialog}
          className="px-4 py-2 bg-gradient-to-r from-accent-blue to-accent-orange text-white rounded-lg hover:opacity-90 flex items-center gap-2"
          disabled={isLoading}
        >
          <span>+</span>
          <span>New Folder</span>
        </button>
      </div>

      {/* Info Banner - Different content for superusers vs regular users */}
      <div className="p-4 bg-accent-blue/10 border-b border-accent-blue/30">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-accent-blue flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1 text-sm text-white">
            {user?.is_superuser ? (
              <>
                <p className="font-medium mb-1">How Denomination Inheritance Works:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-200">
                  <li>Files placed in a folder automatically inherit its denomination settings</li>
                  <li>If a folder has NO denomination assigned, files keep their own metadata</li>
                  <li>When changing folder denomination with existing files, you'll be asked if changes should apply to contents</li>
                </ul>
              </>
            ) : (
              <>
                <p className="font-medium mb-1">Organizing Your Documents:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-200">
                  <li>Create folders to organize your personal documents</li>
                  <li>Documents you upload are private and only visible to you</li>
                  <li>Use folders to categorize documents for easier searching</li>
                </ul>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Selected Folder Details */}
      {selectedFolder && (
        <div className="p-4 bg-dark-card border-b border-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-white">{selectedFolder.folder_path}</h3>
              <div className="flex gap-4 mt-2 text-sm">
                {/* Denomination - Only visible to superusers */}
                {user?.is_superuser && (
                  <div>
                    <span className="text-dark-text-muted">Denomination:</span>{' '}
                    <span className="font-medium text-white">
                      {selectedFolder.denomination_id || (
                        <span className="text-dark-text-muted italic">None (no inheritance)</span>
                      )}
                    </span>
                  </div>
                )}
                <div>
                  <span className="text-dark-text-muted">Type:</span>{' '}
                  <span className="font-medium text-white">
                    {selectedFolder.document_type || <span className="text-dark-text-muted">—</span>}
                  </span>
                </div>
                {/* Official status - Only visible to superusers */}
                {user?.is_superuser && (
                  <div>
                    <span className="text-dark-text-muted">Official:</span>{' '}
                    <span className="font-medium text-white">
                      {selectedFolder.is_official ? 'Yes' : 'No'}
                    </span>
                  </div>
                )}
              </div>
              {selectedFolder.description && (
                <p className="text-sm text-dark-text-muted mt-2">{selectedFolder.description}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Folder Tree */}
      <div className="flex-1 overflow-hidden">
        <FolderTree />
      </div>

      {/* Dialogs */}
      <FolderDialog />
      <FolderWarningDialog />

      {/* Delete Confirmation Dialog */}
      {isDeleteDialogOpen && selectedFolder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-dark-card rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-4 border-b border-dark-border">
              <h2 className="text-xl font-semibold text-white">Delete Folder?</h2>
            </div>
            <div className="p-4">
              <p className="text-dark-text mb-4">
                Are you sure you want to delete the folder{' '}
                <strong className="text-white">"{selectedFolder.folder_path}"</strong>?
              </p>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-sm text-yellow-200">
                  <strong>Note:</strong> This will not delete files in the folder, but they will no
                  longer {user?.is_superuser ? 'inherit denomination settings from this folder path' : 'be organized under this folder'}.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end p-4 border-t border-dark-border">
              <button
                onClick={closeDeleteDialog}
                className="px-4 py-2 text-white bg-dark-card hover:bg-bridge-hover border border-dark-border rounded-lg transition-colors"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
                disabled={isSaving}
              >
                {isSaving ? 'Deleting...' : 'Delete Folder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FolderManagementView;
